import React, { useState, useEffect, useCallback } from 'react';
import { Building2, ChevronDown, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

/**
 * OrganizationSelector - Header component for switching organization context
 * Shows user's current organization and allows switching for multi-org users
 */
const OrganizationSelector = ({ currentOrgId, onOrgChange }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [userContext, setUserContext] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserContext = useCallback(async () => {
    try {
      const res = await api.get('/multiorg/context/me');
      setUserContext(res.data);
      
      // If no current org is selected, use primary
      if (!currentOrgId && res.data.primary_organization) {
        onOrgChange?.(res.data.primary_organization.id);
      }
    } catch (error) {
      console.error('Error fetching user context:', error);
    } finally {
      setLoading(false);
    }
  }, [api, currentOrgId, onOrgChange]);

  useEffect(() => {
    fetchUserContext();
  }, [fetchUserContext]);

  if (loading || !userContext) {
    return null;
  }

  // If user has only one membership, don't show selector
  if (!userContext.memberships || userContext.memberships.length <= 1) {
    const org = userContext.memberships?.[0]?.organization;
    if (!org) return null;
    
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-lg border border-slate-700">
        <Building2 className="w-4 h-4 text-sky-400" />
        <span className="text-sm font-medium truncate max-w-[150px]">{org.name}</span>
      </div>
    );
  }

  // Find current organization
  const currentOrg = userContext.memberships.find(
    m => m.organization?.id === currentOrgId
  )?.organization || userContext.primary_organization;

  // Role labels with colors
  const roleInfo = {
    owner: { label: 'Owner', color: 'bg-purple-500' },
    manager: { label: 'Manager', color: 'bg-blue-500' },
    analyst: { label: 'Analyst', color: 'bg-green-500' },
    reporter: { label: 'Reporter', color: 'bg-yellow-500' },
    viewer: { label: 'Viewer', color: 'bg-slate-500' },
    contractor: { label: 'Contractor', color: 'bg-orange-500' },
    regulator: { label: 'Regulator', color: 'bg-red-500' },
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between bg-slate-800/50 border-slate-700 hover:bg-slate-800"
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="w-4 h-4 text-sky-400 shrink-0" />
            <span className="truncate">{currentOrg?.name || 'Select Organization'}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-slate-900 border-slate-700">
        <Command className="bg-transparent">
          <CommandInput placeholder="Search organizations..." className="border-slate-700" />
          <CommandList>
            <CommandEmpty>No organizations found.</CommandEmpty>
            <CommandGroup heading="My Organizations">
              {userContext.memberships.map((membership) => {
                const org = membership.organization;
                if (!org) return null;
                
                const role = roleInfo[membership.role] || { label: membership.role, color: 'bg-slate-500' };
                const isSelected = currentOrgId === org.id;
                
                return (
                  <CommandItem
                    key={org.id}
                    value={org.name}
                    onSelect={() => {
                      onOrgChange?.(org.id);
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{org.name}</span>
                        {membership.is_primary && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-500 text-amber-400">
                            Primary
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`w-2 h-2 rounded-full ${role.color}`} />
                        <span className="text-xs text-slate-400">{role.label}</span>
                        <span className="text-xs text-slate-500">• {org.code}</span>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default OrganizationSelector;
